import { useQuery, useQueryClient } from "@tanstack/react-query";
import moment from "moment-timezone";
import { z } from "zod";
import { DbUser } from "../models";
import { BaseDbObject } from "../models/db";
import { Customer } from "./reports";
import {
    fetchApiDelete,
    fetchApiGet,
    fetchApiPost,
    fetchApiPut,
} from "./utils";
import { Decimal } from "decimal.js";
import { transformToDecimalStrict, zodStrictRequiredDecimal, zodOptionalDecimal, transformToDate } from "./common";
import { transformToDateStrict } from "./common";
import { CommonDestinationTimezone } from "../utils";

export interface SummaryInfo {
    yearSummary: {
        status: "open" | "closed",
        has_placeholder_data: boolean,
        year: number,
        total_qty: Decimal | string,
        total_marketing_fee: Decimal | string,
    }[],
    monthSummary: {
        status: "open" | "closed",
        has_placeholder_data: boolean,
        year: number,
        month: number,
        month_display: string,
        total_qty: Decimal | string,
        total_marketing_fee: Decimal | string,
    }[],
}

export const DirectSaleReportQueryValidation = z.object({
    status: z.enum(["open", "closed"]).optional(),
    range: z.object({
        start_date: (z.union([z.string(), z.date()])).transform(transformToDateStrict({ throwOnInvalid: true, key: "range.start_date" })),
        end_date: (z.union([z.string(), z.date()])).transform(transformToDateStrict({ throwOnInvalid: true, key: "range.end_date" })),
    }).optional(),
    entry_date: (z.union([z.string(), z.date()])).transform(transformToDateStrict({ throwOnInvalid: true, key: "entry_date" })).optional(),
    gl_date: (z.union([z.string(), z.date()])).transform(transformToDateStrict({ throwOnInvalid: true, key: "gl_date" })).optional(),
});

export const DirectSaleUpdateStatusValidation = z.object({
    id: z.string().uuid(),
    status: z.enum(["open", "closed"]),
});

export const DirectSaleCashPriceLineItemCreateValidation = z.object({
    qty: zodStrictRequiredDecimal({ key: "qty" }).refine((item: any) => {
        if (item == null) return false;
        if (typeof item === "string" && !item.trim()) return false;
        return item.gte(0);
    }, {
        message: "Quantity must be greater than or equal to 0",
        path: ["qty"],
    }),
    uom: z.string(),

    futures: zodOptionalDecimal({ key: "futures" }),
    basis: zodOptionalDecimal({ key: "basis" }),
    has_placeholder_data: z.boolean(),
    average_cash_price: zodOptionalDecimal({ key: "average_cash_price" }),

    marketing_fee_rate: z.number().or(z.string()).transform(transformToDecimalStrict({ throwOnInvalid: true, key: "marketing_fee_rate" })).refine((item: any) => {
        return item.gte(0) && item.lte(100);
    }, {
        message: "Marketing fee rate must be between 0 and 100",
        path: ["marketing_fee_rate"],
    }),
});

export const DirectSaleCreateValidation = z.object({
    trader_id: z.string().uuid(),
    location_id: z.string().uuid(),
    producer_id: z.string().uuid(),

    contract_type: z.string(),
    commodity: z.string(),
    crop_year: z.number(),

    contract_id: z.string(),
    contract_date: z.string().transform(transformToDateStrict({ throwOnInvalid: true, key: "contract_date" })),
    destination: z.string(),

    delivery_start_date: z.string().nullable().optional().transform(transformToDate({ throwOnInvalid: true, key: "delivery_start_date" })),
    delivery_end_date: z.string().nullable().optional().transform(transformToDate({ throwOnInvalid: true, key: "delivery_end_date" })),
    futures_month: z.string().nullable().optional(),

    gl_date: z.string().transform(transformToDateStrict({ throwOnInvalid: true, key: "gl_date" })),
    notes: z.string().optional(),

    offset_against_ap_settlement: z.boolean().optional(),
    should_send_ar_invoice: z.boolean().optional(),

    LineItems: z.array(DirectSaleCashPriceLineItemCreateValidation).min(1),
});

export const DirectSaleUpdateValidation = DirectSaleCreateValidation.extend({
    id: z.string().uuid(),
});


interface DirectSalesParams {
    status?: "open" | "closed";
    location_id?: string;
    producer_id?: string;
    trader_id?: string;
    contract_type?: string;
    commodity?: string;
    futures_month?: string;
    gl_date?: Date;
    entry_date_range?: {
        start: Date;
        end: Date;
    }
}

function assembleDirectSalesQueryParams(params: DirectSalesParams) {
    const queryParams = new URLSearchParams();
    queryParams.set("pageSize", "1000000");

    if (params.status) {
        queryParams.set("status", params.status);
    }
    if (params.location_id) {
        queryParams.set("location_id", params.location_id);
    }
    if (params.producer_id) {
        queryParams.set("producer_id", params.producer_id);
    }
    if (params.trader_id) {
        queryParams.set("trader_id", params.trader_id);
    }

    if (params.commodity) {
        queryParams.set("commodity", params.commodity);
    }
    if (params.futures_month) {
        queryParams.set("futures_month", params.futures_month);
    }
    if (params.gl_date) {
        queryParams.set("gl_date", typeof params.gl_date === "string" ? params.gl_date : params.gl_date.toISOString());
    }
    if (params.entry_date_range?.start && params.entry_date_range?.end) {
        queryParams.set("entry_date_range", JSON.stringify({
            start: moment.tz(params.entry_date_range.start, CommonDestinationTimezone).startOf("day").toISOString(),
            end: moment.tz(params.entry_date_range.end, CommonDestinationTimezone).endOf("day").toISOString()
        }));
    }

    return queryParams;
}

interface DirectSaleCashPriceLineItem extends BaseDbObject {
    direct_sale_id: string;

    qty: Decimal;
    converted_qty: Decimal;
    uom: string;

    futures: Decimal;
    basis: Decimal;

    net_cash_price: Decimal;
    average_cash_price: Decimal | null;

    marketing_fee_rate: Decimal;
    marketing_fee: Decimal;

    has_placeholder_data: boolean;
}


export interface DirectSale {
    id?: string;
    location_id: string;
    producer_id: string;
    trader_id: string;

    has_placeholder_data: boolean;
  
    contract_type: string;
    commodity: string;
    crop_year: number;
  
    contract_id: string;
    gl_date: Date;
    contract_date: Date;
    destination: string;
  
    delivery_start_date: Date;
    delivery_end_date: Date;
    deleted_at?: Date;
  
    futures_month: string;
    notes?: string;
    qty: Decimal;
    uom: string;
    futures: Decimal;
    basis: Decimal;
    net_cash_price: Decimal;
    average_cash_price?: Decimal | null;
    marketing_fee_rate: Decimal;
    marketing_fee: Decimal;

    offset_against_ap_settlement?: boolean;
    should_send_ar_invoice?: boolean;
  
    Location?: Location;
    Producer?: Customer;
    LineItems: DirectSaleCashPriceLineItem[];
}

export function useGetDirectSalesSummary() {
    return useQuery({
        queryKey: ["directSales", "summary"],
        queryFn: () => fetchApiGet<SummaryInfo>(`direct-sales/summary`),
        retry: 1,
    });
}

export function useListDirectSales(filters: DirectSalesParams) {
    return useQuery({
        queryKey: ["directSales", "items", assembleDirectSalesQueryParams(filters).toString()],
        queryFn: () => fetchApiGet< {
            data: DirectSale[];
            pagination: {
                totalItems: number;
                totalPages: number;
                pageSize: number;
                currentPage: number;
            }
        }>(`direct-sales`, assembleDirectSalesQueryParams(filters)),
        retry: 1,
    });
}

export function useCreateDirectSales() {
    const queryClient = useQueryClient();

    return async (params: typeof DirectSaleCreateValidation._type) => {
        const queryParams = new URLSearchParams();

        const result = await fetchApiPost<any>(
            `direct-sales`,
            params,
            queryParams
        );
        if (result.success) {
            queryClient.invalidateQueries({ queryKey: ["directSales"] });
        }

        return result;
    };
}

export function useUpdateDirectSales() {
    const queryClient = useQueryClient();

    return async (params: typeof DirectSaleUpdateValidation._type) => {
        const queryParams = new URLSearchParams();

        const result = await fetchApiPut<any>(
            `direct-sales/${params.id}`,
            params,
            queryParams
        );

        if (result.success) {
            queryClient.invalidateQueries({ queryKey: ["directSales"] });
        }

        return result;
    };
}

export function useArchiveDirectSales() {
    const queryClient = useQueryClient();

    return async (params: typeof DirectSaleUpdateStatusValidation._type) => {
        const queryParams = new URLSearchParams();

        const result = await fetchApiPost<any>(
            `direct-sales/${params.id}/status`,
            params,
            queryParams
        );

        if (result.success) {
            queryClient.invalidateQueries({ queryKey: ["directSales"] });
        }

        return result;
    };
}

export function useGenerateDirectSalesRecentReport() {
    const queryClient = useQueryClient();

    return async (params: typeof DirectSaleReportQueryValidation._type) => {
        const queryParams = new URLSearchParams();

        const result = await fetchApiPost<{ link: string }>(
            `direct-sales/report/recent`,
            params,
            queryParams
        );

        return result;
    };
}

export function useGenerateDirectSalesBreakdownReport() {
    const queryClient = useQueryClient();

    return async (params: typeof DirectSaleReportQueryValidation._type) => {
        const queryParams = new URLSearchParams();

        const result = await fetchApiPost<{ link: string }>(
            `direct-sales/report/breakdown`,
            params,
            queryParams
        );

        return result;
    };
}

export function useGenerateDirectSalesOpenReport() {
    const queryClient = useQueryClient();

    return async () => {
        const queryParams = new URLSearchParams();

        const result = await fetchApiPost<{ link: string }>(
            `direct-sales/report/open/full`,
            {},
            queryParams
        );

        return result;
    };
}


export function useDeleteDirectSale() {
    const queryClient = useQueryClient();

    return async (params: { id: string }) => {
        const queryParams = new URLSearchParams();

        const result = await fetchApiDelete<any>(
            `direct-sales/${params.id}`,
        );

        if (result.success) {
            queryClient.invalidateQueries({ queryKey: ["directSales"] });
        }

        return result;
    };
}
